/**
 * Router Agent for Ameen Insurance Assistant
 * 
 * Main menu agent - routes to policy, claim, or FAQ based on user choice.
 * 
 * TOOLS (EXACT names from backend):
 * - go_to_policy_agent: Routes to policy creation
 * - go_to_claim_agent: Routes to claim filing
 * - search_faq: Searches FAQ knowledge base
 * - go_to_router_agent: Returns to menu (self-reference)
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender, UIStateManager } from '../types';
import { noEntryRequirements } from '../guardrails';
import { logger } from '../../utils/logger';
import { vectorFAQService } from '../../services/vectorFAQService';

/**
 * Create Router Agent node
 */
export function createRouterAgent(voiceGender: VoiceGender): AgentNode {
  return {
    id: 'router_agent',
    name: 'Router Agent',
    description: 'Main menu - routes to policy, claim, or FAQ',

    entryGuardrails: noEntryRequirements(),

    exitGuardrails: {
      transitions: new Map([
        ['go_to_policy', {
          target: { agentId: 'policy_agent' },
          guardrails: []
        }],
        ['go_to_claim', {
          target: { agentId: 'claim_agent' },
          guardrails: []
        }],
        ['go_to_faq', {
          target: { agentId: 'faq_agent' },
          guardrails: []
        }]
      ]),
      fallback: { agentId: 'router_agent' }
    },

    initialPrompt: 'القائمة الرئيسية.',

    /**
     * Handle tool calls - SINGLE SOURCE OF TRUTH for router agent tools
     */
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext,
      uiState?: UIStateManager
    ): Promise<ToolCallResult> => {
      logger.tool('[RouterAgent] Tool called:', toolName, args);

      switch (toolName) {
        // ═══ ROUTING TOOLS ═══
        case 'go_to_policy_agent':
          logger.info('[RouterAgent] Transitioning to policy_agent');
          return {
            toolName,
            args,
            result: { success: true, message: 'Routing to policy creation' },
            triggersTransition: true,
            transitionTo: 'policy_agent',
            transitionDelayMs: -1 // Wait for audio completion before transitioning
          };

        case 'go_to_claim_agent':
          logger.info('[RouterAgent] Transitioning to claim_agent');
          return {
            toolName,
            args,
            result: { success: true, message: 'Routing to claim filing' },
            triggersTransition: true,
            transitionTo: 'claim_agent',
            transitionDelayMs: -1 // Wait for audio completion before transitioning
          };

        case 'go_to_router_agent':
          logger.info('[RouterAgent] Staying at router (self-reference)');
          return {
            toolName,
            args,
            result: { success: true, message: 'Already at menu' }
          };

        // ═══ FAQ TOOL ═══
        case 'search_faq':
          return await handleSearchFAQ(args, toolName);

        default:
          logger.warn('[RouterAgent] Unknown tool:', toolName);
          return {
            toolName,
            args,
            result: { success: false, error: `Unknown tool: ${toolName}` }
          };
      }
    },

    onActivate: async (context: AgentContext) => {
      logger.info('[RouterAgent] Activated');
      // Initialize FAQ service if needed
      await vectorFAQService.initialize().catch(e => {
        logger.error('[RouterAgent] Failed to initialize FAQ service:', e);
      });
    },

    onDeactivate: async (context: AgentContext) => {
      logger.info('[RouterAgent] Deactivated');
    }
  };
}

/**
 * Handle search_faq tool - shared across multiple agents
 */
async function handleSearchFAQ(args: Record<string, any>, toolName: string): Promise<ToolCallResult> {
  const question = args.question;

  if (!question || typeof question !== 'string' || question.trim().length === 0) {
    logger.error('[FAQ] Invalid search query:', question);
    return {
      toolName,
      args,
      result: {
        success: false,
        error: 'معذرة، لم أستطع فهم السؤال. من فضلك حاول مرة أخرى.',
        message: 'The question parameter was empty or invalid.'
      }
    };
  }

  const query = question.trim();
  logger.info('[FAQ] Searching for:', query);

  // Detect language
  const hasArabic = /[\u0600-\u06FF]/.test(query);
  const language = hasArabic ? 'ar' : 'en';

  try {
    await vectorFAQService.initialize();
    const results = await vectorFAQService.search(query, language, 5, 0.25);

    if (results.length > 0) {
      const formattedResults = vectorFAQService.formatResultsForAI(results, language);
      const firstResult = results[0];
      const firstAnswer = language === 'ar' ? firstResult.item.answer : firstResult.item.answer_en;
      const firstQuestion = language === 'ar' ? firstResult.item.question : firstResult.item.question_en;

      logger.info('[FAQ] Found', results.length, 'results');

      return {
        toolName,
        args,
        result: {
          success: true,
          found: true,
          results: formattedResults,
          count: results.length,
          bestMatch: {
            question: firstQuestion,
            answer: firstAnswer,
            confidence: firstResult.similarity
          },
          instruction: language === 'ar'
            ? 'استخدم الإجابة أعلاه للرد على المستخدم. لا تخترع معلومات إضافية.'
            : 'Use the answer above to respond to the user. Do not invent additional information.'
        }
      };
    } else {
      logger.info('[FAQ] No results found for:', query);
      const noResultsMessage = language === 'ar'
        ? `لم يتم العثور على نتائج محددة في قاعدة المعرفة للسؤال: "${query}". يمكنك استخدام معلوماتك العامة.`
        : `No specific results found in the knowledge base for: "${query}". You may use your general knowledge.`;

      return {
        toolName,
        args,
        result: {
          success: true,
          found: false,
          results: noResultsMessage,
          count: 0,
          instruction: 'No results in knowledge base. Answer from general knowledge if appropriate.'
        }
      };
    }
  } catch (error: any) {
    logger.error('[FAQ] Search error:', error);
    const errorMessage = language === 'ar'
      ? 'حدث خطأ أثناء البحث في قاعدة المعرفة. يمكنك الإجابة من معلوماتك العامة.'
      : 'An error occurred while searching the knowledge base. You may answer from general knowledge.';

    return {
      toolName,
      args,
      result: {
        success: false,
        found: false,
        error: errorMessage,
        technicalError: error.message || 'Search failed'
      }
    };
  }
}

// Export the FAQ handler for reuse by other agents
export { handleSearchFAQ };
